import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  InputAdornment,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import useCustomer from '../../hooks/useCustomer';

function ExternalInvoiceDialog({
  open,
  onClose,
  onCreated,
  createExternalInvoice,
  booking,
  onBookingNotPaid,
  onMarkBookingPaidAfterInvoice,
}) {
  const entertainment = booking?.entertainmentId;
  const entertainmentId = entertainment?._id || '';

  const { searchCustomers } = useCustomer();

  const [paymentMode, setPaymentMode] = useState('paid');
  const [externalReceiptNumber, setExternalReceiptNumber] = useState('');
  const [serviceDate, setServiceDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [customerNumberInput, setCustomerNumberInput] = useState('');
  const [customerData, setCustomerData] = useState({
    company: '',
    firstName: '',
    lastName: '',
    mail: '',
    street: '',
    postcode: '',
    city: '',
    addressAddition: '',
    jobCode: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const activeLineItems = useMemo(
    () => (booking?.lineItems || []).filter(li => li.status === 'active'),
    [booking?.lineItems]
  );

  const prevOpenRef = useRef(false);

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (!justOpened) return;

    setPaymentMode('paid');
    setExternalReceiptNumber('');
    setCustomerNumberInput('');
    setServiceDate(
      booking?.startTime
        ? dayjs(booking.startTime).format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD')
    );
    setCustomerData({
      company: '',
      firstName: booking?.firstName || '',
      lastName: booking?.lastName || '',
      mail: booking?.mail || '',
      street: booking?.contactData?.street || '',
      postcode: booking?.contactData?.postcode || '',
      city: booking?.contactData?.city || '',
      addressAddition: '',
      jobCode: '',
    });
  }, [open, booking]);

  const handleCustomerChange = useCallback(e => {
    const { name, value } = e.target;
    setCustomerData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleLookupCustomer = async () => {
    const num = parseInt(customerNumberInput, 10);
    if (Number.isNaN(num)) {
      toast.error('Ungültige Kundennummer');
      return;
    }
    try {
      const res = await searchCustomers(String(num), 1, 5);
      const match = (res?.customers || []).find(c => c.customerNumber === num);
      if (!match) {
        toast.error('Kunde nicht gefunden');
        return;
      }
      setCustomerData({
        company: match.companyName || '',
        firstName: match.firstName || '',
        lastName: match.lastName || '',
        mail: match.email || '',
        street:
          match.street && match.houseNumber
            ? `${match.street} ${match.houseNumber}`
            : match.street || '',
        postcode: match.postalCode || '',
        city: match.city || '',
        addressAddition: match.addressAddition || '',
        jobCode: match.jobCode || '',
      });
      toast.success('Kundendaten geladen');
    } catch {
      // error handled globally in useHttp
    }
  };

  const canSubmit = entertainmentId && activeLineItems.length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    const payload = {
      entertainmentId,
      paymentMode,
      externalReceiptNumber: externalReceiptNumber || undefined,
      serviceDate: serviceDate || undefined,
      relatedBookingId: booking?._id || undefined,
      lineItems: activeLineItems.map(li => ({
        description: li.description,
        count: li.count,
        price: li.price,
        vatRate: 19,
      })),
      customerData,
    };

    const isBookingPaid = Boolean(booking?.fullyPaid) || !(parseFloat(booking?.totalAmount || 0) > 0);

    if (paymentMode === 'paid' && !isBookingPaid) {
      onBookingNotPaid?.(payload);
      return;
    }

    setSubmitting(true);
    try {
      const invoice = await createExternalInvoice(payload);
      toast.success(
        paymentMode === 'paid'
          ? 'Bezahlte Rechnung erfolgreich erstellt.'
          : 'Offene Rechnung zur Überweisung erstellt.'
      );
      onCreated?.(invoice);
      onClose();
    } catch (err) {
      toast.error('Fehler beim Erstellen der Rechnung.');
    } finally {
      setSubmitting(false);
    }
  };

  const entertainmentLabel = entertainment?.entertainmentName || entertainment?.longName || '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>Rechnung erstellen</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {entertainmentLabel && (
            <Grid item xs={12}>
              <Typography variant='body2' color='text.secondary'>
                Entertainment: <strong>{entertainmentLabel}</strong>
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Rechnungstyp
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={paymentMode}
              onChange={(_, v) => v && setPaymentMode(v)}
              size='small'
            >
              <ToggleButton value='paid'>Bezahlt (Kasse)</ToggleButton>
              <ToggleButton value='unpaid'>Offen (Überweisung)</ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {paymentMode === 'paid' && (
            <Grid item xs={12}>
              <TextField
                label='Bon-Nummer (extern)'
                fullWidth
                value={externalReceiptNumber}
                onChange={e => setExternalReceiptNumber(e.target.value)}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Kundendaten
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label='Kundennummer'
              size='small'
              fullWidth
              value={customerNumberInput}
              onChange={e => setCustomerNumberInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLookupCustomer()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton size='small' onClick={handleLookupCustomer} edge='end'>
                      <SearchIcon fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label='Firma'
              name='company'
              fullWidth
              size='small'
              value={customerData.company}
              onChange={handleCustomerChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label='Vorname'
              name='firstName'
              fullWidth
              size='small'
              value={customerData.firstName}
              onChange={handleCustomerChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label='Nachname'
              name='lastName'
              fullWidth
              size='small'
              value={customerData.lastName}
              onChange={handleCustomerChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label='E-Mail'
              name='mail'
              type='email'
              fullWidth
              size='small'
              value={customerData.mail}
              onChange={handleCustomerChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label='Straße und Nr.'
              name='street'
              fullWidth
              size='small'
              value={customerData.street}
              onChange={handleCustomerChange}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label='PLZ'
              name='postcode'
              fullWidth
              size='small'
              value={customerData.postcode}
              onChange={handleCustomerChange}
            />
          </Grid>
          <Grid item xs={8}>
            <TextField
              label='Ort'
              name='city'
              fullWidth
              size='small'
              value={customerData.city}
              onChange={handleCustomerChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label='Adresszusatz'
              name='addressAddition'
              fullWidth
              size='small'
              value={customerData.addressAddition}
              onChange={handleCustomerChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label='Referenz / Jobcode'
              name='jobCode'
              fullWidth
              size='small'
              value={customerData.jobCode}
              onChange={handleCustomerChange}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Abbrechen
        </Button>
        <Button variant='contained' onClick={handleSubmit} disabled={!canSubmit}>
          {paymentMode === 'paid' ? 'Bezahlte Rechnung erstellen' : 'Offene Rechnung erstellen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ExternalInvoiceDialog;
